import React from 'react';
import './AboutPage.css';
import whyWeBuiltProblemsMildImg from '../../images/why-we-built-problems-mild.png';
import whyWeBuiltBeliefFunImg from '../../images/why-we-built-belief-fun.png';
import whatMakesDifferentImg from '../../images/what-makes-different.png';
import whoWeSupportImg from '../../images/who-we-support.png';

const AboutPage = () => (
  <>
    <div className="about-page">
      <section className="mission-section">
        <div className="container">
          <div className="mission-content">
            <div className="mission-text">
              <h2>Our Philosophy</h2>
              <p>At Solace Care 360, we believe meaningful care does not end when the appointment does.</p>
              <p>Chronic and complex conditions require continuity - not just visits. They require reinforcement, monitoring, coordination, and trust. They require a system that supports patients between touchpoints while protecting the clinical integrity of the practice.</p>
              <p>We exist to build that system.</p>
              <p>Solace Care 360 was created to serve as a trusted extension of your clinical team - bringing structure, consistency, and nurse-led expertise to care management in a way that feels seamless, not supplemental.</p>
            </div>
          </div>
        </div>
      </section>
      <section className="why-we-built-section">
        <div className="container">
          <div className="why-we-built-content">
            <h2 className="why-we-built-title">Why We Built Solace</h2>
            <div className="why-we-built-row">
              <div className="why-we-built-graphic">
                <img src={whyWeBuiltProblemsMildImg} alt="" className="why-we-built-graphic-img" />
              </div>
              <div className="why-we-built-block why-we-built-block-right">
                <p className="why-we-built-intro">Too often, care management programs are added as an afterthought.</p>
                <ul className="why-we-built-problems">
                  <li>They rely on inconsistent workflows</li>
                  <li>They burden already stretched teams</li>
                  <li>They prioritize billing before structure</li>
                  <li>They promise engagement without operational discipline</li>
                </ul>
              </div>
            </div>
            <div className="why-we-built-row">
              <div className="why-we-built-block why-we-built-block-left">
                <p className="why-we-built-belief">We believe care management should be built differently. It should be:</p>
                <ul className="why-we-built-list">
                  <li>Clinically grounded</li>
                  <li>Operationally sound</li>
                  <li>Thoughtfully documented</li>
                  <li>Sustainably designed</li>
                  <li>Aligned with real-world workflows</li>
                </ul>
              </div>
              <div className="why-we-built-graphic">
                <img src={whyWeBuiltBeliefFunImg} alt="" className="why-we-built-graphic-img" />
              </div>
            </div>
            <p className="why-we-built-close">Solace Care 360 was built with that standard in mind.</p>
          </div>
        </div>
      </section>
      <section className="approach-section">
        <div className="container">
          <div className="approach-row">
            <div className="approach-content">
              <h2 className="approach-title">Our Approach</h2>
              <p className="approach-intro">Our model is called Care 360 because we look at the full picture - clinical, operational, and human.</p>
              <p className="approach-nurses">Our nurses provide ongoing patient engagement, proactive assessment, coordination across providers, education reinforcement, and timely escalation when needed.</p>
              <p className="approach-different">But what sets our approach apart is not just what we do - it is how we do it.</p>
              <ul className="approach-with-list">
                <li>With structure.</li>
                <li>With accountability.</li>
                <li>With alignment to your practice's standards.</li>
              </ul>
              <p className="approach-close">We are intentional about documentation, communication, and workflow integration - ensuring care management supports both quality outcomes and program sustainability.</p>
            </div>
          </div>
        </div>
      </section>
      <section className="what-makes-section">
        <div className="container">
          <div className="what-makes-row">
            <div className="what-makes-content">
              <h2 className="what-makes-title">What Makes Us Different</h2>
              <p className="what-makes-intro">Solace Care 360 is nurse-led by design.</p>
              <p className="what-makes-body">Our model is shaped by clinical leadership and built around professional judgment, not scripts. We prioritize meaningful engagement over volume-based outreach.</p>
              <p className="what-makes-body">We integrate with your workflow rather than operating parallel to it. We align with your quality goals, documentation standards, and reimbursement requirements - without disrupting your clinical rhythm.</p>
              <p className="what-makes-close">We are a structured clinical operations partner focused on long-term durability and patient-centered continuity.</p>
            </div>
            <div className="what-makes-graphic">
              <img src={whatMakesDifferentImg} alt="" className="what-makes-graphic-img" />
            </div>
          </div>
        </div>
      </section>
      <section className="who-we-support-section">
        <div className="container">
          <h2>Who We Partner With</h2>
          <p className="who-we-support-blurb">Solace Care 360 partners with healthcare organizations that want scalable, high-quality care management support, including:</p>
          <ul className="who-we-support-list">
            <li>Primary care practices</li>
            <li>Specialty practices</li>
            <li>Independent practices and group practices</li>
            <li>Multi-site clinics</li>
            <li>Health systems seeking scalable care management support</li>
          </ul>
          <p className="who-we-support-blurb who-we-support-subblurb">Our services are ideal for practices that want to:</p>
          <ul className="who-we-support-list">
            <li>Improve patient engagement between visits</li>
            <li>Enhance quality metrics and outcomes</li>
            <li>Extend care beyond the office without adding in-office staff</li>
            <li>Ensure consistent, compliant care management delivery</li>
            <li>Build a program that is sustainable, structured, and scalable</li>
          </ul>
        </div>
      </section>
      <section className="patients-support-section">
        <div className="container">
          <div className="patients-support-row">
            <div className="patients-support-graphic">
              <img src={whoWeSupportImg} alt="" className="patients-support-graphic-img" />
            </div>
            <div className="patients-support-content">
              <h2 className="patients-support-title">Who We Support</h2>
              <p className="patients-support-intro">Our model is designed for patients who:</p>
              <ul className="patients-support-list">
                <li>Live with chronic or complex conditions</li>
                <li>Require consistent follow-up and reinforcement</li>
                <li>Benefit from structured education and coaching</li>
                <li>Need coordination across providers and settings</li>
                <li>Are at risk for complications or avoidable escalation</li>
              </ul>
              <p className="patients-support-close">We meet patients where they are - by phone, secure messaging, and remote monitoring when appropriate - always grounded in professionalism and compassion.</p>
            </div>
          </div>
        </div>
      </section>
      <section className="commitment-section">
        <div className="container">
          <div className="commitment-content">
            <h2 className="commitment-title">Our Commitment</h2>
            <p className="commitment-intro">Care management is not a box to check.</p>
            <p className="commitment-body">It is a responsibility - to patients, to providers, and to the integrity of the healthcare system.</p>
            <p className="commitment-close">At Solace Care 360, we are committed to delivering care management that reflects clinical excellence, operational discipline, and meaningful human connection - from every angle.</p>
          </div>
        </div>
      </section>
    </div>
  </>
);

export default AboutPage;
